import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  updateFlashBar,
  updateIsLoading,
  update_breadcrumb,
} from '../../redux/action';
import instance from '../../service/axiosInstane';
import { flashBarTheme } from '../../config/flashBar';
import { StateProps } from '../../redux/reducer';
import { Role } from '../../config/enum';
import { colors } from '../../config/constants';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import BarChart from '../../components/BarChart/BarChart';

type GraphProps = {
  name: string;
  value: number;
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const role = useSelector((state: StateProps) => state.tokenInfo.userInfo.role);
  const userInfo = useSelector((state: StateProps) => state.tokenInfo.userInfo);
  const [state, setState] = useState({
    websites: 0,
    organizations: 0,
    fileStores: 0,
    users: 0,
    categories: 0,
    keywords: 0,
    roles: 0,
    dmcaFile: 0,
  });
  const [weblinksStatistics, setWeblinkStatistics] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [processStatistics, setProcessStatistics] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [crawlingStatistics, setCrawlingStatistics] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [siteCrawledByYears, setSiteCrawledByYears] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [totalCrawledByYears, setTotalCrawledByYears] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [crawlingCompleted, setCrawlingComplted] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [crawlingPending, setCrawlingPending] = useState<GraphProps[]>([
    { name: '', value: 0 },
  ]);
  const [archived, setArchived] = useState<GraphProps[]>([{ name: '', value: 0 }]);
  const [showYear, setShowYear] = useState(false);
  const currentYear = new Date().getFullYear();

  const [year, setYear] = useState(currentYear);
  const [isLoading, setIsLoading] = useState(false);
  const yearOperation = () => {
    const years = [];
    for (let i = 0; i <= 3; i++) {
      years.push(currentYear - i);
    }
    return years;
  };

  useEffect(() => {
    const breadcrumb = [{ path: '/dashboard', name: 'Dashboard' }];
    dispatch(update_breadcrumb(breadcrumb));
    yearOperation();
  }, []);

  const superAdminData = [
    {
      lable: 'Websites',
      value: state.websites,
      icon: 'fa-solid fa-globe',
      color: 'text-[#5800FF]',
      url: '/websitesSuper',
    },
    {
      lable: 'Users',
      value: state.users,
      icon: 'fa-solid fa-users',
      color: 'text-[#0096FF]',
      url: '/users',
    },
    {
      lable: 'Organizations',
      value: state.organizations,
      icon: 'fa-solid fa-building-columns',
      color: 'text-[#00D7FF]',
      url: '/organization',
    },
    {
      lable: 'FileStores',
      value: state.fileStores,
      icon: 'fa-solid fa-folder-open',
      color: 'text-[#72FFFF]',
      url: '/server',
    },
  ];

  const adminData = [
    {
      lable: 'Websites',
      value: state.websites,
      icon: 'fa-solid fa-globe',
      color: 'text-[#5800FF]',
      url: '/websiteAdmin',
    },
    {
      lable: 'Role',
      value: state.roles,
      icon: 'fa-solid fa-chalkboard-user',
      color: 'text-[#00D7FF]',
      url: '/roles',
    },
    {
      lable: 'Users',
      value: state.users,
      icon: 'fa-solid fa-users',
      color: 'text-[#0096FF]',
      url: '/users',
    },
    {
      lable: 'Categories',
      value: state.categories,
      icon: 'fa-solid fa-layer-group',
      color: 'text-[#72FFFF]',
      url: '/categories',
    },
    {
      lable: 'Keywords',
      value: state.keywords,
      icon: 'fa-solid fa-key',
      color: 'text-[#337CCF]',
      url: '/categories',
    },

    {
      lable: 'DMCA ',
      value: state.dmcaFile,
      icon: 'fa-solid fa-folder-open',
      color: 'text-[#512B81]',
      url: '/dmcaFiles',
    },
  ];

  useEffect(() => {
    if (role === Role.SuperAdmin) {
      getAllReports();
    } else {
      getAdminReports();
      getProcessStatistics();
      getWeblinkStatistics();
      getCrawlingStatistics();
    }
  }, []);

  useEffect(() => {
    siteCrawledByYear(year);
    totalPageCrawledByYear(year);
  }, [year]);

  const getAllReports = async () => {
    dispatch(updateIsLoading(true));
    await instance
      .get('/reports/resource-counts')
      .then((response) => {
        if (response.status === 200) {
          setState(response.data);
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const getAdminReports = async () => {
    dispatch(updateIsLoading(true));
    await instance
      .get(`/reports/organizations/${userInfo.organizationId}/resource-counts`)
      .then((response) => {
        if (response.status === 200) {
          setState(response.data);
          dispatch(updateIsLoading(false));
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        dispatch(updateIsLoading(false));
      });
  };

  const getProcessStatistics = async () => {
    setIsLoading(true);
    await instance
      .get(`reports/organizations/${userInfo.organizationId}/process-statistics`)
      .then((response) => {
        if (response.status === 200) {
          setProcessStatistics(response.data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        setIsLoading(false);
      });
  };

  const getWeblinkStatistics = async () => {
    setIsLoading(true);
    await instance
      .get(`reports/organizations/${userInfo.organizationId}/weblinks-statistics`)
      .then((response) => {
        if (response.status === 200) {
          setWeblinkStatistics(
            response.data.filter(
              (item: GraphProps) =>
                item.name !== 'Archived' && item.name !== 'NotArchived'
            )
          );
          setArchived(
            response.data.filter(
              (item: GraphProps) =>
                item.name !== 'DMCA Notified' && item.name !== 'DMCA Pending'
            )
          );
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        setIsLoading(false);
      });
  };

  const getCrawlingStatistics = async () => {
    setIsLoading(true);
    await instance
      .get(`reports/organizations/${userInfo.organizationId}/crawling-statistics`)
      .then((response) => {
        if (response.status === 200) {
          setCrawlingStatistics(
            response.data.filter((item: GraphProps) => item.name !== 'Completed')
          );
          setCrawlingComplted(
            response.data.filter((item: GraphProps) => item.name === 'Completed')
          );

          setCrawlingPending(
            response.data.filter((item: GraphProps) => item.name === 'Pending')
          );

          setIsLoading(false);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        setIsLoading(false);
      });
  };

  const siteCrawledByYear = async (year: number) => {
    setIsLoading(true);
    await instance
      .get(
        `${
          role === Role.SuperAdmin
            ? `reports/sites-crawled-superadmin?year=${year}`
            : `reports/organizations/${userInfo.organizationId}/sites-crawled-by-year?year=${year}`
        }`
      )
      .then((response) => {
        if (response.status === 200) {
          setSiteCrawledByYears(response.data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        setIsLoading(false);
      });
  };

  const totalPageCrawledByYear = async (year: number) => {
    setIsLoading(true);
    await instance
      .get(
        `${
          role === Role.SuperAdmin
            ? `reports/totalpages-crawled-superadmin?year=${year}`
            : `reports/organizations/${userInfo.organizationId}/totalpages-crawled-by-year?year=${year}`
        }`
      )
      .then((response) => {
        if (response.status === 200) {
          setTotalCrawledByYears(response.data);
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const obj = {
          isOpen: true,
          message: err.response.data.Message,
          type: flashBarTheme.ERROR,
        };
        dispatch(updateFlashBar(obj));
        setIsLoading(false);
      });
  };

  const LoadingDiv = () => (
    <div className="sm:flex mt-2 self-stretch justify-center items-center space-x-4 h-44 w-full ">
      <div role="status">
        <svg
          aria-hidden="true"
          className="inline w-14 h-14 mr-2 text-gray-200 animate-spin dark:text-gray-600 fill-blue-600"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
        <span className="sr-only">Loading...</span>
      </div>
    </div>
  );

  return (
    <div className="p-2">
      <div className="bg-white px-4 shadow-md rounded-sm">
        <div className="h-[1px] w-full mb-4"></div>
        <div className="sm:w-full sm:flex items-center justify-between">
          {role === Role.SuperAdmin && (
            <div className="w-full grid grid-cols-2 gap-1 md:grid-cols-2 lg:grid-cols-4 sm:grid-cols-2 sm:gap-2">
              {superAdminData.map((item, index) => {
                return (
                  <Link
                    to={item.url}
                    key={index}
                    className="flex items-center space-x-2 p-2 w-[200px] "
                  >
                    <div className="w-fit">
                      <i className={`${item.icon} text-[48px] ${item.color}`}></i>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[2px] h-10 bg-[#176b87] mx-2"></div>
                      <div>
                        <div
                          className={` ${colors.primaryText} text-sm font-semibold`}
                        >
                          {item.lable}
                        </div>
                        <div className="font-semibold text-[22px] text-[#02134d]">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          {role === Role.Admin && (
            <div className="w-full grid grid-cols-2 gap-1 md:grid-cols-3 lg:grid-cols-6 sm:grid-cols-3 sm:gap-2">
              {adminData.map((item, index) => {
                return (
                  <Link
                    to={item.url}
                    key={index}
                    className="flex items-center space-x-2 p-2 w-[200px] "
                  >
                    <div className="w-fit">
                      <i className={`${item.icon} text-[48px] ${item.color}`}></i>
                    </div>
                    <div className="flex items-center">
                      <div className="w-[2px] h-10 bg-[#176b87] mx-2"></div>
                      <div>
                        <div
                          className={` ${colors.primaryText} text-sm font-semibold`}
                        >
                          {item.lable}
                        </div>
                        <div className="font-semibold text-[22px] text-[#02134d]">
                          {item.value}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
          {role === Role.User && (
            <div className="w-full grid grid-cols-2 gap-1 md:grid-cols-4 lg:grid-cols-4 sm:grid-cols-3 sm:gap-2">
              {adminData
                .filter((item) => item.lable !== 'Role' && item.lable !== 'Users')
                .map((item, index) => {
                  return (
                    <Link
                      to={item.url}
                      key={index}
                      className="flex items-center space-x-2 p-2 w-[200px] "
                    >
                      <div className="w-fit">
                        <i className={`${item.icon} text-[48px] ${item.color}`}></i>
                      </div>
                      <div className="flex items-center">
                        <div className="w-[2px] h-10 bg-[#176b87] mx-2"></div>
                        <div>
                          <div
                            className={` ${colors.primaryText} text-sm font-semibold`}
                          >
                            {item.lable}
                          </div>
                          <div className="font-semibold text-[22px] text-[#02134d]">
                            {item.value}
                          </div>
                        </div>
                      </div>
                    </Link>
                  );
                })}
            </div>
          )}
        </div>
        <div className="h-[1px] w-full bg-[#e6e8e7] mt-4"></div>
      </div>

      {role !== Role.SuperAdmin && (
        <>
          <div className="lg:flex items-center justify-between lg:space-x-2">
            <div className="lg:flex mt-2 lg:w-1/2 w-full">
              <div className=" text-white sm:w-full bg-white shadow-lg p-4 ">
                <div className={` p-1 font-bold text-[#02134d]`}>
                  Crawling Statistics
                </div>
                <div className="h-[1px] mb-2 w-full bg-[#e6e8e7]"></div>
                {isLoading ? (
                  LoadingDiv()
                ) : (
                  <BarChart
                    graphData={crawlingStatistics}
                    label={'Crawling Statistics'}
                    height={200}
                    type={'bar'}
                  />
                )}
              </div>
            </div>
            <div className="lg:flex mt-2 w-full lg:w-1/2 self-stretch  justify-between lg:space-x-4">
              <div className="w-full bg-white flex flex-col justify-center items-center p-4">
                <div className="w-full bg-slate-50 border border-blue-300 flex flex-col justify-center items-center p-4">
                  <div className="flex items-center space-x-3">
                    <div>
                      <i className="fa-solid fa-check text-[32px] text-[#116A7B]"></i>
                    </div>
                    <div className="text-[32px] text-[#116A7B]">
                      {crawlingCompleted[0].value}
                    </div>
                  </div>
                  <div className="text-[16px] px-4 py-1 text-[#116A7B] rounded-sm ">
                    Crawling Completed
                  </div>
                </div>
                <div className="w-full bg-slate-50 border border-blue-300 flex flex-col justify-center items-center p-4 mt-2">
                  <div className="flex items-center space-x-3">
                    <div>
                      <i className="fa-solid fa-clock text-[32px] text-[#CDC2AE]"></i>
                    </div>
                    <div className="text-[32px] text-[#CDC2AE]">
                      {crawlingPending[0]?.value}
                    </div>
                  </div>
                  <div className="text-[16px] px-4 py-1 text-[#116A7B] rounded-sm">
                    Crawling Pending
                  </div>
                </div>
              </div>
              <div className="w-full bg-white flex flex-col justify-center items-center p-4">
                <div className="w-full bg-slate-50 border border-blue-300 flex flex-col justify-center items-center p-4">
                  <div className="flex items-center space-x-3">
                    <div>
                      <i className="fa-solid fa-box-archive text-[32px] text-[#116A7B]"></i>
                    </div>
                    <div className="text-[32px] text-[#116A7B]">
                      {archived[0]?.value ? archived[0]?.value : 0}
                    </div>
                  </div>
                  <div className="text-[16px] px-4 py-1 text-[#116A7B] rounded-sm ">
                    Archived
                  </div>
                </div>
                <div className="w-full bg-slate-50 border border-blue-300 flex flex-col justify-center items-center p-4 mt-2">
                  <div className="flex items-center space-x-3">
                    <div>
                      <i className="fa-solid fa-box-open  text-[32px] text-[#CDC2AE]"></i>
                    </div>
                    <div className="text-[32px] text-[#CDC2AE]">
                      {archived[1]?.value ? archived[1]?.value : 0}
                    </div>
                  </div>
                  <div className="text-[16px] px-4 py-1 text-[#116A7B] rounded-sm">
                    Not Archived
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="sm:flex items-center justify-between sm:space-x-2">
            <div className="sm:flex mt-2 sm:w-1/2">
              <div className=" text-white sm:w-full bg-white shadow-lg p-4 ">
                <div className={` p-1 font-bold text-[#02134d]`}>
                  Process Statistics
                </div>
                <div className="h-[1px] mb-2 w-full bg-[#e6e8e7]"></div>
                {isLoading ? (
                  LoadingDiv()
                ) : (
                  <BarChart
                    graphData={processStatistics}
                    label={'Process Statistics'}
                    height={200}
                    type={'bar'}
                  />
                )}
              </div>
            </div>
            <div className="sm:flex mt-2 sm:w-1/2">
              <div className=" text-white sm:w-full bg-white shadow-lg p-4 ">
                <div className={` p-1 font-bold text-[#02134d]`}>
                  DMCA statistics
                </div>
                <div className="h-[1px] mb-2 w-full bg-[#e6e8e7]"></div>
                {isLoading ? (
                  LoadingDiv()
                ) : (
                  <BarChart
                    graphData={weblinksStatistics}
                    label={'Weblink Statistics'}
                    height={200}
                    type={'bar'}
                  />
                )}
              </div>
            </div>
          </div>
        </>
      )}

      <div className="flex items-center justify-between mt-2 pl-1 relative">
        {' '}
        <div className="font-semibold text-[#02134d]">Monthly Reports</div>
        <div
          onClick={() => setShowYear(true)}
          className="font-semibold cursor-pointer px-2 py-[2px] border border-[#02134d] text-sm rounded-sm text-[#02134d]"
        >
          <div className="flex items-center justify-between w-[50px]">
            {' '}
            {year}
            <i className="fa-solid fa-caret-down"></i>
          </div>
        </div>
        {showYear && (
          <ClickAwayListener onClickAway={() => setShowYear(false)}>
            <div
              onClick={() => setShowYear(false)}
              className="absolute right-0 top-8 bg-white cursor-pointer border text-sm"
            >
              {yearOperation().map((item, index) => {
                return (
                  <div
                    key={index}
                    onClick={() => setYear(item)}
                    className="hover:bg-blue-900 hover:text-white px-4 py-[2px]"
                  >
                    {item}
                  </div>
                );
              })}
            </div>
          </ClickAwayListener>
        )}
      </div>
      <div className="sm:flex items-center justify-between sm:space-x-2 text-[#02134d]">
        <div className="sm:flex mt-2 sm:w-full">
          <div className="sm:w-full bg-white shadow-lg p-4 ">
            <div className="flex items-center justify-between  ">
              {' '}
              <div className={`p-1 font-bold`}>Site Crawled</div>
            </div>

            <div className="h-[1px] mb-2 w-full bg-[#e6e8e7]"></div>
            {isLoading ? (
              LoadingDiv()
            ) : (
              <BarChart
                graphData={siteCrawledByYears}
                label={'Site Crawled'}
                height={120}
                type={'line'}
              />
            )}
          </div>
        </div>
      </div>
      <div className="sm:flex items-center justify-between sm:space-x-2 text-[#02134d]">
        <div className="sm:flex mt-2 sm:w-full">
          <div className="sm:w-full bg-white shadow-lg p-4 ">
            <div className="flex items-center justify-between relative ">
              {' '}
              <div className={`p-1 font-bold`}>Page Crawled</div>
            </div>

            <div className="h-[1px] mb-2 w-full bg-[#e6e8e7]"></div>
            {isLoading ? (
              LoadingDiv()
            ) : (
              <BarChart
                graphData={totalCrawledByYears}
                label={'Page Crawled'}
                height={120}
                type={'line'}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
